import { spawnRuntime } from '../spawnRuntime.js';

const SIZE = 160;
const PADDING = 10;

const state = {
  canvas: null,
  ctx: null,
  roomBounds: { minX: -10, maxX: 10, minZ: -10, maxZ: 10 },
  scaleX: 1,
  scaleY: 1,
  doorPos: null,
  startedAtMs: 0,
};

function clamp01(v) {
  return Math.max(0, Math.min(1, v));
}

function recalcScale() {
  const roomW = Math.max(0.001, state.roomBounds.maxX - state.roomBounds.minX);
  const roomH = Math.max(0.001, state.roomBounds.maxZ - state.roomBounds.minZ);
  state.scaleX = (SIZE - PADDING * 2) / roomW;
  state.scaleY = (SIZE - PADDING * 2) / roomH;
}

function clearCanvas(ctx) {
  ctx.clearRect(0, 0, SIZE, SIZE);
  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  ctx.fillRect(0, 0, SIZE, SIZE);
}

function drawRoom(ctx) {
  const width = SIZE - PADDING * 2;
  const height = SIZE - PADDING * 2;
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = 'rgba(255,255,255,0.6)';
  ctx.strokeRect(PADDING, PADDING, width, height);
}

function drawProps(ctx, props) {
  if (!Array.isArray(props) || props.length === 0) return;
  ctx.fillStyle = 'rgba(255,255,255,0.2)';
  for (const p of props) {
    const minX = Number(p?.minX);
    const maxX = Number(p?.maxX);
    const minZ = Number(p?.minZ);
    const maxZ = Number(p?.maxZ);
    if (!Number.isFinite(minX) || !Number.isFinite(maxX) || !Number.isFinite(minZ) || !Number.isFinite(maxZ)) continue;
    const a = minimap.worldToMinimap(minX, minZ);
    const b = minimap.worldToMinimap(maxX, maxZ);
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    const w = Math.max(1, Math.abs(b.x - a.x));
    const h = Math.max(1, Math.abs(b.y - a.y));
    ctx.fillRect(x, y, w, h);
  }
}

function drawEnemies(ctx, enemies) {
  if (!Array.isArray(enemies) || enemies.length === 0) return;
  ctx.fillStyle = '#ff4444';
  for (const enemy of enemies) {
    const pos = minimap.worldToMinimap(enemy.x, enemy.z);
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 3, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawPlayer(ctx, playerPos, playerRotationY) {
  if (!playerPos) return;
  const p = minimap.worldToMinimap(playerPos.x, playerPos.z);
  const r = 5;
  const angle = (Number(playerRotationY) || 0) + Math.PI;
  const p0x = p.x + Math.sin(angle) * r;
  const p0y = p.y - Math.cos(angle) * r;
  const p1x = p.x + Math.sin(angle + 2.4) * r * 0.8;
  const p1y = p.y - Math.cos(angle + 2.4) * r * 0.8;
  const p2x = p.x + Math.sin(angle - 2.4) * r * 0.8;
  const p2y = p.y - Math.cos(angle - 2.4) * r * 0.8;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(p0x, p0y);
  ctx.lineTo(p1x, p1y);
  ctx.lineTo(p2x, p2y);
  ctx.closePath();
  ctx.fill();
}

function drawDoor(ctx) {
  if (!state.doorPos || !spawnRuntime.isRoomComplete()) return;
  const pos = minimap.worldToMinimap(state.doorPos.x, state.doorPos.z);
  const t = (performance.now() - state.startedAtMs) / 1000;
  const opacity = 0.4 + 0.6 * clamp01((Math.sin(t * Math.PI * 2) + 1) * 0.5);
  ctx.fillStyle = `rgba(0,255,136,${opacity.toFixed(3)})`;
  ctx.fillRect(pos.x - 3, pos.y - 3, 6, 6);
}

export const minimap = {
  init(canvasElement, roomBounds) {
    if (!canvasElement) return;
    state.canvas = canvasElement;
    state.ctx = canvasElement.getContext('2d');
    state.roomBounds = roomBounds || state.roomBounds;
    state.startedAtMs = performance.now();
    recalcScale();
  },

  update(playerPos, playerRotationY, enemies, props) {
    const ctx = state.ctx;
    if (!ctx) return;
    clearCanvas(ctx);
    drawRoom(ctx);
    drawProps(ctx, props);
    drawEnemies(ctx, enemies);
    drawPlayer(ctx, playerPos, playerRotationY);
    drawDoor(ctx);
  },

  setDoorPosition(pos) {
    state.doorPos = pos ? { x: Number(pos.x) || 0, z: Number(pos.z) || 0 } : null;
  },

  worldToMinimap(worldX, worldZ) {
    const x = PADDING + (worldX - state.roomBounds.minX) * state.scaleX;
    const y = PADDING + (worldZ - state.roomBounds.minZ) * state.scaleY;
    return { x, y };
  },
};
